import React, { useState } from 'react';
import Icon from './AppIcon';

import { getPermissionDenialReason } from '../utils/rolePermissions';

/**
 * Restricted Feature Wrapper
 * Displays features in grayed-out state with permission warnings
 */
const RestrictedFeature = ({
  hasAccess,
  feature,
  children,
  showWarning = true,
  warningMessage,
  className = '',
  onClick
}) => {
  const [showModal, setShowModal] = useState(false);

  // If user has access, render normally
  if (hasAccess) {
    return children;
  }

  // Get appropriate warning message
  const message = warningMessage || getPermissionDenialReason(feature);

  // Handle click on restricted feature
  const handleClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (onClick) {
      onClick(e);
    } else {
      setShowModal(true);
    }
  };

  return (
    <>
      <div className={`relative ${className}`}>
        {/* Grayed-out overlay */}
        <div 
          className="relative opacity-50 pointer-events-none select-none"
          aria-disabled="true"
        >
          {children}
        </div>
        
        {/* Clickable overlay with lock icon */}
        <div 
          className="absolute inset-0 flex items-center justify-center bg-gray-900/5 cursor-not-allowed rounded-lg"
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-label="Restricted feature - Officer access only"
        >
          <div className="bg-white rounded-full p-3 shadow-lg border-2 border-amber-400">
            <Icon name="Lock" size={24} className="text-amber-600" strokeWidth={2} />
          </div>
        </div>

        {/* Inline warning badge */}
        {showWarning && (
          <div className="absolute top-2 right-2 z-10">
            <div className="bg-amber-500 text-white text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 shadow-lg">
              <Icon name="ShieldAlert" size={12} strokeWidth={2} />
              Officer Only
            </div>
          </div>
        )}
      </div>
      {/* Modal for detailed permission warning */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-start gap-4 mb-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-100 flex-shrink-0">
                <Icon name="Lock" size={24} className="text-amber-600" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Officer Access Required
                </h3>
                <p className="text-sm text-gray-700">
                  {message}
                </p>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> This feature is restricted to MyBodyToday officers for security and administrative purposes. If you believe you should have access, please contact your administrator.
              </p>
            </div>

            <button
              onClick={() => setShowModal(false)}
              className="w-full px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors focus:outline-none focus:ring-3 focus:ring-primary/30"
            >
              Understood
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RestrictedFeature;
